import { useState, useEffect, useRef } from 'react'
import { ListChecks, FlaskConical, Check, TrendingUp, Calculator } from 'lucide-react'
import { useLab } from '../../../context/LabContext'
import InfoBox from '../InfoBox'

const steps = [
  { id: 1, title: 'Plot Calibration Curve', short: 'Click to generate plot' },
  { id: 2, title: 'Calculate Linear Regression', short: 'Fit line to data' },
  { id: 3, title: 'Determine Unknown', short: 'Interpolate concentration' },
  { id: 4, title: 'Calculate Final Result', short: 'Calculate mg phosphate per serving' }
]

// Standard concentrations (molar) and expected absorbances
const STANDARDS = [
  { conc: 0, label: 'Blank', absorbance: 0.000 },
  { conc: 0.00002, label: '2.0×10⁻⁵ M', absorbance: 0.020 },
  { conc: 0.00005, label: '5.0×10⁻⁵ M', absorbance: 0.050 },
  { conc: 0.0001, label: '1.0×10⁻⁴ M', absorbance: 0.100 },
  { conc: 0.0005, label: '5.0×10⁻⁴ M', absorbance: 0.500 },
  { conc: 0.00075, label: '7.5×10⁻⁴ M', absorbance: 0.750 }
]

const UNKNOWN_ABSORBANCE = 0.350 // typical cola sample

const Part4Analysis = () => {
  const { state, updateExp3Part4, completeStep, update } = useLab()
  const { exp3Part4, exp3Part3, currentStep, labMode, completedSteps } = state

  const [showInfo, setShowInfo] = useState<number | null>(null)
  const [plotAnimationPhase, setPlotAnimationPhase] = useState(0)
  const [showRegression, setShowRegression] = useState(false)
  const [showUnknown, setShowUnknown] = useState(false)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  // Fix: Reset currentStep to first incomplete step when this part mounts
  useEffect(() => {
    if (labMode !== 'guided') return
    const firstIncomplete = steps.find(s => !completedSteps.includes(`analysis-${s.id}`))
    const targetStep = firstIncomplete ? firstIncomplete.id : steps[steps.length - 1].id
    if (currentStep !== targetStep) {
      update({ currentStep: targetStep })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const isActive = (s: number) => labMode === 'free' || currentStep === s
  const isDone = (s: number) => completedSteps.includes(`analysis-${s}`)

  // Get readings from Part 3 or use defaults
  const readings = exp3Part3?.readings || {}
  const readingKeys = ['blank', 'std1', 'std2', 'std3', 'std4', 'std5']
  const standardData = STANDARDS.map((s, i) => ({
    conc: s.conc,
    label: s.label,
    absorbance: readings[readingKeys[i]] || s.absorbance
  }))
  const unknownAbsorbance = readings['unknown'] || UNKNOWN_ABSORBANCE

  // Max concentration for axis scaling
  const maxConc = 0.00075

  // Calculate linear regression
  const calculateRegression = () => {
    const n = standardData.length
    let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0, sumY2 = 0

    standardData.forEach(point => {
      sumX += point.conc
      sumY += point.absorbance
      sumXY += point.conc * point.absorbance
      sumX2 += point.conc * point.conc
      sumY2 += point.absorbance * point.absorbance
    })

    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX)
    const intercept = (sumY - slope * sumX) / n

    // Calculate R-squared
    const yMean = sumY / n
    let ssTotal = 0, ssResidual = 0
    standardData.forEach(point => {
      const yPredicted = slope * point.conc + intercept
      ssTotal += Math.pow(point.absorbance - yMean, 2)
      ssResidual += Math.pow(point.absorbance - yPredicted, 2)
    })
    const r2 = 1 - (ssResidual / ssTotal)

    return { slope, intercept, r2 }
  }

  // Draw calibration curve on canvas
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const width = canvas.width
    const height = canvas.height
    const padding = 50

    // Clear canvas
    ctx.fillStyle = '#1A1A1A'
    ctx.fillRect(0, 0, width, height)

    // Draw axes
    ctx.strokeStyle = '#4A4A4A'
    ctx.lineWidth = 2
    ctx.beginPath()
    ctx.moveTo(padding, padding)
    ctx.lineTo(padding, height - padding)
    ctx.lineTo(width - padding, height - padding)
    ctx.stroke()

    // Axis labels
    ctx.fillStyle = '#CCCCCC'
    ctx.font = '12px Arial'
    ctx.textAlign = 'center'
    ctx.fillText('Concentration (×10⁻⁴ M)', width / 2, height - 10)

    ctx.save()
    ctx.translate(15, height / 2)
    ctx.rotate(-Math.PI / 2)
    ctx.fillText('Absorbance', 0, 0)
    ctx.restore()

    // Grid lines and tick marks
    ctx.strokeStyle = '#333333'
    ctx.lineWidth = 1

    // X-axis ticks (0 to maxConc M)
    const xTickConcs = [0, 0.0001, 0.0002, 0.0003, 0.0004, 0.0005, 0.0006, 0.00075]
    for (let i = 0; i < xTickConcs.length; i++) {
      const c = xTickConcs[i]
      const x = padding + (c / maxConc) * (width - 2 * padding)
      ctx.beginPath()
      ctx.moveTo(x, height - padding)
      ctx.lineTo(x, height - padding + 5)
      ctx.stroke()
      ctx.fillStyle = '#999999'
      const label = c === 0 ? '0' : c >= 0.001 ? c.toFixed(4) : (c * 10000).toFixed(1)
      ctx.fillText(label, x, height - padding + 18)

      // Grid line
      ctx.strokeStyle = '#2A2A2A'
      ctx.beginPath()
      ctx.moveTo(x, height - padding)
      ctx.lineTo(x, padding)
      ctx.stroke()
      ctx.strokeStyle = '#333333'
    }

    // Y-axis ticks (0-0.8 absorbance)
    for (let i = 0; i <= 8; i++) {
      const y = height - padding - (i / 8) * (height - 2 * padding)
      ctx.beginPath()
      ctx.moveTo(padding, y)
      ctx.lineTo(padding - 5, y)
      ctx.stroke()
      ctx.fillStyle = '#999999'
      ctx.textAlign = 'right'
      ctx.fillText((i * 0.1).toFixed(1), padding - 10, y + 4)

      // Grid line
      ctx.strokeStyle = '#2A2A2A'
      ctx.beginPath()
      ctx.moveTo(padding, y)
      ctx.lineTo(width - padding, y)
      ctx.stroke()
      ctx.strokeStyle = '#333333'
    }

    // Plot data points (animated)
    if (plotAnimationPhase >= 1) {
      const pointsToShow = Math.min(plotAnimationPhase, standardData.length)
      for (let i = 0; i < pointsToShow; i++) {
        const point = standardData[i]
        const x = padding + (point.conc / maxConc) * (width - 2 * padding)
        const y = height - padding - (point.absorbance / 0.8) * (height - 2 * padding)

        ctx.fillStyle = '#FFE678'
        ctx.beginPath()
        ctx.arc(x, y, 6, 0, Math.PI * 2)
        ctx.fill()

        ctx.fillStyle = '#FFFFFF'
        ctx.beginPath()
        ctx.arc(x, y, 3, 0, Math.PI * 2)
        ctx.fill()
      }
    }

    // Draw regression line
    if (showRegression) {
      const regression = calculateRegression()
      const x1 = padding
      const y1 = height - padding - (regression.intercept / 0.8) * (height - 2 * padding)
      const x2 = width - padding
      const y2 = height - padding - ((regression.slope * maxConc + regression.intercept) / 0.8) * (height - 2 * padding)

      ctx.strokeStyle = '#00A86B'
      ctx.lineWidth = 2
      ctx.setLineDash([5, 5])
      ctx.beginPath()
      ctx.moveTo(x1, y1)
      ctx.lineTo(x2, y2)
      ctx.stroke()
      ctx.setLineDash([])

      // Regression equation
      ctx.fillStyle = '#00A86B'
      ctx.font = '11px Courier New'
      ctx.textAlign = 'left'
      ctx.fillText(`y = ${regression.slope.toFixed(4)}x + ${regression.intercept.toFixed(4)}`, padding + 10, padding + 20)
      ctx.fillText(`R² = ${regression.r2.toFixed(4)}`, padding + 10, padding + 35)
    }

    // Draw unknown sample interpolation
    if (showUnknown) {
      const regression = calculateRegression()
      const unknownConc = (unknownAbsorbance - regression.intercept) / regression.slope

      const xUnknown = padding + (unknownConc / maxConc) * (width - 2 * padding)
      const yUnknown = height - padding - (unknownAbsorbance / 0.8) * (height - 2 * padding)

      // Horizontal line from Y-axis
      ctx.strokeStyle = '#FF5A36'
      ctx.lineWidth = 1.5
      ctx.setLineDash([3, 3])
      ctx.beginPath()
      ctx.moveTo(padding, yUnknown)
      ctx.lineTo(xUnknown, yUnknown)
      ctx.stroke()

      // Vertical line to X-axis
      ctx.beginPath()
      ctx.moveTo(xUnknown, yUnknown)
      ctx.lineTo(xUnknown, height - padding)
      ctx.stroke()
      ctx.setLineDash([])

      // Unknown point
      ctx.fillStyle = '#FF5A36'
      ctx.beginPath()
      ctx.arc(xUnknown, yUnknown, 8, 0, Math.PI * 2)
      ctx.fill()
      ctx.fillStyle = '#FFFFFF'
      ctx.beginPath()
      ctx.arc(xUnknown, yUnknown, 4, 0, Math.PI * 2)
      ctx.fill()

      // Label
      ctx.fillStyle = '#FF5A36'
      ctx.font = 'bold 11px Arial'
      ctx.textAlign = 'left'
      ctx.fillText(`Unknown: ${(unknownConc * 1000).toFixed(3)} mM`, xUnknown + 10, yUnknown - 10)
    }
  }, [plotAnimationPhase, showRegression, showUnknown, standardData, unknownAbsorbance])

  // Handlers
  const handlePlotCalibration = () => {
    if (!isActive(1)) return

    // Animate plotting points
    let phase = 0
    const interval = setInterval(() => {
      phase++
      setPlotAnimationPhase(phase)
      if (phase >= standardData.length) {
        clearInterval(interval)
        completeStep('analysis-1')
      }
    }, 400)
  }

  const handleCalculateRegression = () => {
    if (!isActive(2) || !isDone(1)) return

    const regression = calculateRegression()
    updateExp3Part4({
      calibrationCurve: {
        slope: regression.slope,
        intercept: regression.intercept,
        r2: regression.r2
      }
    })
    setShowRegression(true)
    completeStep('analysis-2')
  }

  const handleDetermineUnknown = () => {
    if (!isActive(3) || !isDone(2)) return

    const regression = calculateRegression()
    const concentration = (unknownAbsorbance - regression.intercept) / regression.slope

    updateExp3Part4({
      unknownAbsorbance: unknownAbsorbance,
      calculatedConcentration: concentration
    })
    setShowUnknown(true)
    completeStep('analysis-3')
  }

  const handleCalculateFinal = () => {
    if (!isActive(4) || !isDone(3)) return

    updateExp3Part4({ calibrationComplete: true })
    completeStep('analysis-4')
  }

  const completedCount = completedSteps.filter((s: string) => s.startsWith('analysis')).length

  // Calculate values for display
  const regression = isDone(2) ? calculateRegression() : null
  const unknownConcentration = exp3Part4.calculatedConcentration ||
    (regression ? (unknownAbsorbance - regression.intercept) / regression.slope : null)

  // Final calculation: phosphoric acid concentration in original cola
  // Cola was diluted 50-fold prior to analysis, so multiply measured concentration by 50
  // Convert from molarity to mg/L: M * MW_H3PO4(97.99) * 1000 = mg/L
  const originalConcentration = unknownConcentration ? unknownConcentration * 50 : null

  return (
    <div className="part4-analysis">
      {/* Steps Panel */}
      <div className="steps-panel">
        <div className="steps-header">
          <ListChecks size={16} />
          <h3>Part IV: Data Analysis</h3>
        </div>
        <div className="steps-list">
          {steps.map(s => (
            <div
              key={s.id}
              className={`step-item ${isActive(s.id) && !isDone(s.id) ? 'active' : ''}`}
            >
              <div className={`step-number ${isDone(s.id) ? 'done' : isActive(s.id) ? 'current' : ''}`}>
                {isDone(s.id) ? <Check size={12} /> : s.id}
              </div>
              <div className="step-content">
                <div className="step-title-row">
                  <span className={`step-title ${isActive(s.id) ? 'highlight' : ''}`}>
                    {s.title}
                  </span>
                  <button
                    className="step-info-btn"
                    onClick={(e) => { e.stopPropagation(); setShowInfo(s.id) }}
                  >
                    ?
                  </button>
                </div>
                {isActive(s.id) && !isDone(s.id) && (
                  <span className="step-hint">{s.short}</span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Data Table */}
        <div className="data-table">
          <h4>Calibration Data</h4>
          <table>
            <thead>
              <tr>
                <th>Conc (M)</th>
                <th>Absorbance</th>
              </tr>
            </thead>
            <tbody>
              {standardData.map((point, i) => (
                <tr key={i}>
                  <td>{point.label}</td>
                  <td>{point.absorbance.toFixed(3)}</td>
                </tr>
              ))}
              <tr className="unknown-row">
                <td>Unknown</td>
                <td>{unknownAbsorbance.toFixed(3)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Lab Bench */}
      <div className="lab-bench">
        <div className="bench-header">
          <div className="bench-title">
            <FlaskConical size={16} />
            <h3>Data Analysis</h3>
          </div>
          <span className="bench-progress">{completedCount}/4 Complete</span>
        </div>

        <div className="bench-area">
          {/* Calibration Curve Canvas */}
          <div className="graph-container">
            <h4>Calibration Curve: Absorbance vs. Concentration</h4>
            <canvas
              ref={canvasRef}
              width={500}
              height={350}
              className="calibration-canvas"
            />

            {/* Action buttons */}
            <div className="action-buttons">
              {!isDone(1) && (
                <button
                  className="action-button plot-btn"
                  onClick={handlePlotCalibration}
                  disabled={!isActive(1)}
                >
                  <TrendingUp size={18} />
                  Plot Data Points
                </button>
              )}

              {isDone(1) && !isDone(2) && (
                <button
                  className="action-button regression-btn"
                  onClick={handleCalculateRegression}
                  disabled={!isActive(2)}
                >
                  <TrendingUp size={18} />
                  Calculate Best Fit Line
                </button>
              )}

              {isDone(2) && !isDone(3) && (
                <button
                  className="action-button unknown-btn"
                  onClick={handleDetermineUnknown}
                  disabled={!isActive(3)}
                >
                  <Calculator size={18} />
                  Determine Unknown Concentration
                </button>
              )}

              {isDone(3) && !isDone(4) && (
                <button
                  className="action-button final-btn"
                  onClick={handleCalculateFinal}
                  disabled={!isActive(4)}
                >
                  <Calculator size={18} />
                  Calculate Final Result
                </button>
              )}
            </div>
          </div>

          {/* Results Panel */}
          {(regression || unknownConcentration) && (
            <div className="results-panel">
              {regression && (
                <div className="result-card">
                  <h5>Linear Regression</h5>
                  <div className="result-row">
                    <span>Slope (m):</span>
                    <span className="value">{regression.slope.toFixed(4)} A/M</span>
                  </div>
                  <div className="result-row">
                    <span>Intercept (b):</span>
                    <span className="value">{regression.intercept.toFixed(4)}</span>
                  </div>
                  <div className="result-row">
                    <span>R²:</span>
                    <span className="value good">{regression.r2.toFixed(4)}</span>
                  </div>
                </div>
              )}

              {unknownConcentration && (
                <div className="result-card highlight">
                  <h5>Unknown Sample</h5>
                  <div className="result-row">
                    <span>Absorbance:</span>
                    <span className="value">{unknownAbsorbance.toFixed(3)}</span>
                  </div>
                  <div className="result-row">
                    <span>Concentration:</span>
                    <span className="value primary">{unknownConcentration.toFixed(4)} M</span>
                  </div>
                  {originalConcentration && isDone(4) && (
                    <div className="result-row final">
                      <span>Original cola [PO₄] (50× dilution):</span>
                      <span className="value primary large">{(originalConcentration * 1000).toFixed(2)} mM</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Completion message */}
          {isDone(4) && (
            <div className="completion-message">
              <Check size={32} />
              <span>Experiment Complete!</span>
              <p>
                The cola sample contains approximately <strong>{unknownConcentration?.toFixed(4)} M</strong> phosphate (measured),
                which is <strong>{originalConcentration ? (originalConcentration * 1000).toFixed(2) : '---'} mM</strong> in the original cola (accounting for 50-fold dilution).
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Info Box */}
      {showInfo && (
        <InfoBox
          step={showInfo}
          experimentId="exp3"
          stepInfo={getInfoContent(showInfo)}
          onClose={() => setShowInfo(null)}
        />
      )}

      <style>{`
        .part4-analysis {
          display: flex;
          gap: 16px;
          flex: 1;
          width: 100%;
          min-height: 0;
          padding: 16px;
        }

        .steps-panel {
          width: 240px;
          background: var(--bg-surface);
          border-radius: 12px;
          border: 1px solid var(--border);
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .steps-header {
          background: linear-gradient(135deg, var(--primary), var(--primary-dark));
          padding: 14px 16px;
          display: flex;
          align-items: center;
          gap: 10px;
          color: white;
        }

        .steps-header h3 {
          font-size: 14px;
          font-weight: 600;
        }

        .steps-list {
          flex: 1;
          overflow-y: auto;
          padding: 12px;
        }

        .step-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 10px 12px;
          border-radius: 8px;
          margin-bottom: 4px;
          transition: background 0.15s;
        }

        .step-item.active {
          background: rgba(255, 90, 54, 0.15);
        }

        .step-number {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: 600;
          flex-shrink: 0;
          background: var(--border);
          color: var(--text-muted);
        }

        .step-number.done {
          background: var(--secondary);
          color: white;
        }

        .step-number.current {
          background: var(--primary);
          color: white;
        }

        .step-content {
          flex: 1;
          min-width: 0;
        }

        .step-title-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }

        .step-title {
          font-size: 12px;
          font-weight: 600;
        }

        .step-title.highlight {
          color: var(--primary-light);
        }

        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
          border: none;
          cursor: pointer;
        }

        .step-info-btn:hover {
          transform: scale(1.1);
        }

        .step-hint {
          font-size: 11px;
          color: var(--text-secondary);
          margin-top: 4px;
          display: block;
        }

        .data-table {
          padding: 12px;
          border-top: 1px solid var(--border);
          background: var(--bg-elevated);
        }

        .data-table h4 {
          font-size: 11px;
          font-weight: 600;
          color: var(--text-secondary);
          margin: 0 0 8px 0;
          text-transform: uppercase;
        }

        .data-table table {
          width: 100%;
          border-collapse: collapse;
          font-size: 11px;
        }

        .data-table th {
          text-align: left;
          padding: 6px 8px;
          border-bottom: 1px solid var(--border);
          color: var(--text-muted);
        }

        .data-table td {
          padding: 6px 8px;
          border-bottom: 1px solid var(--border);
        }

        .data-table .unknown-row {
          background: rgba(255, 90, 54, 0.1);
        }

        .data-table .unknown-row td {
          color: var(--primary);
          font-weight: 600;
        }

        .lab-bench {
          flex: 1;
          background: var(--bg-surface);
          border-radius: 12px;
          border: 1px solid var(--border);
          display: flex;
          flex-direction: column;
          overflow: auto;
        }

        .bench-header {
          background: linear-gradient(135deg, var(--secondary), var(--secondary-dark));
          padding: 12px 16px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          color: white;
        }

        .bench-title {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .bench-title h3 {
          font-size: 14px;
          font-weight: 600;
        }

        .bench-progress {
          font-size: 12px;
          opacity: 0.9;
        }

        .bench-area {
          flex: 1;
          background: var(--bg-elevated);
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 20px;
          overflow-y: auto;
          gap: 20px;
        }

        .graph-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
        }

        .graph-container h4 {
          font-size: 14px;
          font-weight: 600;
          color: var(--text-primary);
          margin: 0;
        }

        .calibration-canvas {
          border: 2px solid var(--border);
          border-radius: 8px;
          background: #1A1A1A;
        }

        .action-buttons {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          justify-content: center;
        }

        .action-button {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 20px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          border: none;
          transition: all 0.2s;
        }

        .action-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .plot-btn {
          background: #FFE678;
          color: #333;
        }

        .plot-btn:hover:not(:disabled) {
          background: #FFD93D;
        }

        .regression-btn {
          background: var(--secondary);
          color: white;
        }

        .regression-btn:hover:not(:disabled) {
          background: var(--secondary-dark);
        }

        .unknown-btn {
          background: var(--primary);
          color: white;
        }

        .unknown-btn:hover:not(:disabled) {
          background: var(--primary-dark);
        }

        .final-btn {
          background: #9C27B0;
          color: white;
        }

        .final-btn:hover:not(:disabled) {
          background: #7B1FA2;
        }

        .results-panel {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
          justify-content: center;
        }

        .result-card {
          background: var(--bg-surface);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 16px;
          min-width: 200px;
        }

        .result-card.highlight {
          border-color: var(--primary);
          background: rgba(255, 90, 54, 0.05);
        }

        .result-card h5 {
          font-size: 12px;
          font-weight: 600;
          color: var(--text-secondary);
          margin: 0 0 12px 0;
          text-transform: uppercase;
        }

        .result-row {
          display: flex;
          justify-content: space-between;
          font-size: 12px;
          margin-bottom: 6px;
        }

        .result-row.final {
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px solid var(--border);
        }

        .result-row .value {
          font-family: 'Courier New', monospace;
          font-weight: 600;
        }

        .result-row .value.good {
          color: var(--secondary);
        }

        .result-row .value.primary {
          color: var(--primary);
        }

        .result-row .value.large {
          font-size: 16px;
        }

        .completion-message {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
          padding: 24px;
          background: rgba(0, 168, 107, 0.15);
          border-radius: 12px;
          border: 2px solid var(--secondary);
          text-align: center;
          max-width: 500px;
        }

        .completion-message svg {
          color: var(--secondary);
        }

        .completion-message span {
          font-size: 18px;
          font-weight: 600;
          color: var(--secondary);
        }

        .completion-message p {
          font-size: 13px;
          color: var(--text-secondary);
          margin: 0;
          line-height: 1.5;
        }

        .completion-message strong {
          color: var(--primary);
        }
      `}</style>
    </div>
  )
}

// Info content for each step
function getInfoContent(step: number) {
  const content: Record<number, { title: string; content: string }> = {
    1: {
      title: 'Beer-Lambert Law',
      content: 'The amount of light absorbed is related to the concentration by the Beer-Lambert law: A = abc, where A is absorbance, b is solution path length, c is concentration and a is the extinction coefficient. Plot absorbance (ordinate, y) versus concentration of phosphate (abscissa, x). A straight line passing through the origin should be obtained.'
    },
    2: {
      title: 'Linear Regression',
      content: 'Linear regression finds the best-fit line through the data points. The equation y = mx + b gives the slope (m) and intercept (b). R\u00b2 (coefficient of determination) indicates how well the line fits the data; values close to 1.000 indicate excellent linearity.'
    },
    3: {
      title: 'Calculating Unknown Concentration',
      content: 'Using the equation of the line from the calibration curve, calculate the concentration of the unknown. Account for any dilutions you may have made (50-fold dilution). Average the three cola sample results.'
    },
    4: {
      title: 'Lab Questions',
      content: '1. Graph the phosphate standard curve (absorbance vs. concentration). Using the equation of the line, calculate the concentration of the unknown. Account for any dilutions. Label axes and provide a descriptive title. (5 marks)\n\n2. You have 18 M H₂SO₄ — you need to make 100 mL of 0.45 M H₂SO₄. How is this best done? Include all calculations. (1 mark)\n\n3. Using calculations determine how you would make 525 mL of a 0.50 M solution of Cl⁻ ions using CaCl₂. (1 mark)'
    }
  }
  return content[step]
}

export default Part4Analysis
